import { FC, useEffect, useState } from 'react';
import type { MouseEvent } from 'react';
import './ValidateSessionInfoContainer.scss';
import useUrlParams from '../../hooks/useUrlParams';
import useSatellite from '../../hooks/useSatellite';
import useScenario from '../../hooks/useScenario';
import { getSetupsBySatellite, getGroundStationProviders } from '../../../validate/api/validate.api';
import GroundStationService from '../../../../service/GroundStationService';
import { ACP_SATOS_VERSION } from '../../../../release-version';
import type { ValidateSetup, ValidateSession } from '../../../validate/types/validateDetails.types';
import { CloseIcon } from './Svgs';

// ─── Props ────────────────────────────────────────────────────────────────────

interface Props {
  open: boolean;
  close: (e: MouseEvent<HTMLButtonElement>) => void;
}

// ─── Row helper ───────────────────────────────────────────────────────────────

function InfoRow({ label, value }: { label: string; value: string | undefined }) {
  return (
    <div className="vsi__row">
      <span className="vsi__row__label">{label}</span>
      <span className="vsi__row__value">{value || '—'}</span>
    </div>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

const ValidateSessionInfoContainer: FC<Props> = ({ open, close }) => {
  const { mission_id, sat_grp_id, sat_id, scenario_id } = useUrlParams();
  const { selected_satellite } = useSatellite();
  const { scenario } = useScenario();

  const [matchedSetup,   setMatchedSetup]   = useState<ValidateSetup   | null>(null);
  const [matchedSession, setMatchedSession] = useState<ValidateSession  | null>(null);
  const [gspName,        setGspName]        = useState<string>('');

  // Fetch setups → find the setup containing the current session (scenario_id)
  useEffect(() => {
    if (!mission_id || !sat_grp_id || !sat_id) return;
    let cancelled = false;

    getSetupsBySatellite(mission_id, sat_grp_id, sat_id).then(({ data }) => {
      if (cancelled) return;

      let foundSetup: ValidateSetup | null = null;
      let foundSession: ValidateSession | null = null;

      if (scenario_id) {
        for (const setup of data) {
          const session = setup.sessions.find((s) => s.sessionId === scenario_id);
          if (session) { foundSetup = setup; foundSession = session; break; }
        }
      }

      // Fallback: first setup with a running session, or just first setup
      if (!foundSetup) {
        foundSetup = data.find((s) => s.sessions.some((sess) => sess.status === 'RUNNING')) ?? data[0] ?? null;
        foundSession = foundSetup?.sessions.find((s) => s.status === 'RUNNING') ?? foundSetup?.sessions[0] ?? null;
      }

      setMatchedSetup(foundSetup);
      setMatchedSession(foundSession);
    });

    return () => { cancelled = true; };
  }, [mission_id, sat_grp_id, scenario_id]);

  // Fetch provider name once we have the setup's groundStationProviderId
  useEffect(() => {
    const providerId = matchedSetup?.groundStationProviderId;
    if (!providerId) {
      setGspName('');
      return;
    }

    let cancelled = false;

    const resolveGspName = async () => {
      // 1. Direct name from setup payload
      const directName =
        matchedSetup?.groundStationProviderName ||
        (matchedSetup as any)?.providerName ||
        (matchedSetup as any)?.gspName;
      if (directName) {
        if (!cancelled) setGspName(directName);
        return;
      }

      const cleanTargetId = String(providerId).trim().toLowerCase();

      // 2. Try getGroundStationProviders with satellite/group IDs
      try {
        const satIds = [sat_grp_id, sat_id].filter(Boolean) as string[];
        for (const sId of satIds) {
          const { data } = await getGroundStationProviders(sId);
          if (cancelled) return;
          const match = data?.find(
            (p) => String(p.value).trim().toLowerCase() === cleanTargetId
          );
          if (match?.label) {
            setGspName(match.label);
            return;
          }
        }
      } catch {
        // Fallback to next resolution strategy
      }

      // 3. Try GroundStationService.getAllGsProviders for mission
      if (mission_id) {
        try {
          const res = await GroundStationService.getAllGsProviders(mission_id);
          if (cancelled) return;
          const list = Array.isArray(res?.data) ? res.data : [];
          const match = list.find((p: any) => {
            const pid = String(p?.uniqueId || p?.providerId || p?.id || '').trim().toLowerCase();
            return pid === cleanTargetId;
          });
          const name = match?.providerName || match?.name || match?.label;
          if (name) {
            setGspName(name);
            return;
          }
        } catch {
          // Fallback to direct provider query
        }
      }

      // 4. Try GroundStationService.getGsProvider by ID
      try {
        const res = await GroundStationService.getGsProvider(providerId);
        if (cancelled) return;
        const p = res?.data?.data || res?.data;
        const name = p?.providerName || p?.name || p?.label;
        if (name) {
          setGspName(name);
          return;
        }
      } catch {
        // Ignored
      }

      // 5. Fallback: never show the raw internal UUID to the user
      if (!cancelled) {
        setGspName('—');
      }
    };

    resolveGspName();

    return () => {
      cancelled = true;
    };
  }, [mission_id, sat_id, sat_grp_id, matchedSetup?.groundStationProviderId, matchedSetup?.groundStationProviderName]);

  const modelName = selected_satellite?.label ?? selected_satellite?.value?.satelliteName;
  const modelType = matchedSetup?.modelType;
  const modelMode = matchedSetup?.modelMode;
  const satos     = ACP_SATOS_VERSION;

  return (
    <div
      className={`vsi__container${open ? ' vsi__container--active' : ''}`}
      onClick={(e) => e.stopPropagation()}
    >
      {/* ── Header ── */}
      <div className="vsi__header">
        <span className="vsi__title">Session Setup</span>
        <button className="vsi__close" onClick={close} aria-label="Close">
          <CloseIcon />
        </button>
      </div>

      {/* ── Rows ── */}
      <div className="vsi__rows">
        <InfoRow label="Model Name" value={modelName} />
        <InfoRow label="Model Type" value={modelType} />
        <InfoRow label="Model Mode" value={modelMode} />
        <InfoRow label="GSP"        value={gspName}   />
        <InfoRow label="Satos"      value={satos}     />
      </div>
    </div>
  );
};

export default ValidateSessionInfoContainer;
